import {GroupedVirtuoso} from 'react-virtuoso';
import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonItem,
    IonItemDivider,
    IonLabel,
    IonModal,
    IonNote,
    IonProgressBar,
    IonTitle,
    IonToolbar,
    isPlatform
} from '@ionic/react';
import {IonInfiniteScrollCustomEvent} from '@ionic/core/dist/types/components';
import React, {memo, useCallback, useMemo} from 'react';
import {EVSession, useEVChargeHistoryQuery} from '../query/evChargersQuery';
import {useCurrentUserQuery} from '../query/currentUserQuery';
import {useGatewayConnectionStateQuery} from '../query/gatewayConnectionStateQuery';

type EVChargeHistoryModalProps = {
    isOpen: boolean;
    onDidDismiss: () => void;
};

function formatDuration(totalSeconds: number | null) {
    if (totalSeconds === null || totalSeconds < 0) {
        return null;
    }

    const totalMinutes = Math.round(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function EVChargeHistoryItem({session, formatTime}: {
    session: EVSession;
    formatTime: (timestamp: string) => string;
}) {
    const details = [];
    const duration = formatDuration(session.durationSeconds);
    const energyKwh = session.energyKwh === null ? null : Number(session.energyKwh);

    if (!session.timestampStop) {
        details.push('In progress');
    }
    if (duration) {
        details.push(duration);
    }
    if (energyKwh !== null && Number.isFinite(energyKwh) && energyKwh >= 0) {
        details.push(`${energyKwh.toFixed(1)} kWh`);
    }

    const connectorSuffix = '-AB'[session.connectorId] || ` #${session.connectorId}`;

    return (
        <IonItem>
            <IonLabel className="ion-text-wrap">
                <IonNote>{formatTime(session.timestampStart)}</IonNote>
                <h2>{session.chargeStationName || session.chargeStationId}{connectorSuffix}</h2>
                {details.length > 0 && <p>{details.join(' · ')}</p>}
                {session.userName && <p>Started by {session.userName}</p>}
            </IonLabel>
        </IonItem>
    );
}

const EVChargeHistoryModal: React.FC<EVChargeHistoryModalProps> = ({isOpen, onDidDismiss}) => {
    const {data: currentUser, isPending: currentUserPending} = useCurrentUserQuery();
    const historyQuery = useEVChargeHistoryQuery(isOpen && !!currentUser?.id);
    const {isConnecting} = useGatewayConnectionStateQuery();
    const sessions = useMemo(() => historyQuery.data?.pages.flat() ?? [], [historyQuery.data]);

    const dateFormat = useMemo(() => Intl.DateTimeFormat('default', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    }), []);
    const timeFormat = useMemo(() => Intl.DateTimeFormat('default', {
        hour: 'numeric',
        minute: 'numeric'
    }), []);

    const formatDate = useCallback((timestamp: string) => {
        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? 'Unknown date' : dateFormat.format(date);
    }, [dateFormat]);
    const formatTime = useCallback((timestamp: string) => {
        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? '' : timeFormat.format(date);
    }, [timeFormat]);

    const {groupCounts, groups} = useMemo(() => {
        const groupCounts: number[] = [];
        const groups: string[] = [];
        let lastDate: string | undefined;

        for (const session of sessions) {
            const date = formatDate(session.timestampStart);
            if (date !== lastDate) {
                lastDate = date;
                groups.push(date);
                groupCounts.push(0);
            }
            groupCounts[groupCounts.length - 1]++;
        }

        return {groupCounts, groups};
    }, [formatDate, sessions]);

    const loadMore = useCallback(async (event: IonInfiniteScrollCustomEvent<void>) => {
        try {
            await historyQuery.fetchNextPage();
        } finally {
            await event.target.complete();
        }
    }, [historyQuery]);

    const Footer = useCallback(() => (
        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!historyQuery.hasNextPage} threshold="100px">
            <IonInfiniteScrollContent/>
        </IonInfiniteScroll>
    ), [historyQuery.hasNextPage, loadMore]);

    const showScrollableHistory = !!currentUser?.id && sessions.length > 0;

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onDidDismiss}>
            <IonHeader translucent={isPlatform('capacitor')} collapse="fade">
                <IonToolbar>
                    <IonTitle>EV Charge History</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={onDidDismiss}>Done</IonButton>
                    </IonButtons>
                    {!!(currentUserPending || historyQuery.isFetching || isConnecting)
                        && <IonProgressBar type="indeterminate"/>}
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen={true} scrollY={!showScrollableHistory}>
                {!currentUser?.id
                    ? !currentUserPending && (
                        <div className="ion-padding">
                            <IonNote>Sign in to view EV charge history.</IonNote>
                        </div>
                    )
                    : historyQuery.isError
                        ? (
                            <div className="ion-padding">
                                <IonNote color="danger">EV charge history is unavailable.</IonNote>
                            </div>
                        )
                        : sessions.length
                            ? (
                                <GroupedVirtuoso
                                    className="ion-content-scroll-host"
                                    groupCounts={groupCounts}
                                    itemContent={(index: number) => (
                                        <EVChargeHistoryItem session={sessions[index]} formatTime={formatTime}/>
                                    )}
                                    groupContent={(index: number) => (
                                        <IonItemDivider>{groups[index]}</IonItemDivider>
                                    )}
                                    components={{Footer}}
                                />
                            )
                            : !historyQuery.isPending && (
                                <div className="ion-padding">
                                    <IonNote>No charging sessions yet.</IonNote>
                                </div>
                            )}
            </IonContent>
        </IonModal>
    );
};

export default memo(EVChargeHistoryModal);
